import { useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { isRealDate, without } from '@/components/catalog/form-utils';
import { ApiError, api } from '@/lib/api';
import type { Cycle, Intake, IntakeCreateInput, IntakeUpdateInput } from '@/lib/types';
import { Button, Field, Input, Modal, Select } from '@/components/ui';

export interface IntakeFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Omitted = create mode. Provided = edit mode. */
  intake?: Intake;
  /** The first page of cycles, already loaded by the page for its filter. */
  cycles: Cycle[];
  cyclesLoading: boolean;
  /**
   * True when the cycle lookup FAILED, as opposed to returning nothing.
   *
   * The two are indistinguishable from `cycles` alone — both arrive as `[]` —
   * and here that difference decides whether the form can be used at all: the
   * cycle is required, so a failed read leaves a REQUIRED picker with nothing
   * in it, which reads as "there are no cycles yet" and then as a Save button
   * that does not work. Say which one happened.
   */
  cyclesFailed: boolean;
  /** Preselected on create when the list is filtered to one cycle. */
  defaultCycleId?: number;
  onSaved: (intake: Intake) => void;
}

/** `intake_name` is a TEXT column; the schema caps it at 10000. */
const NAME_MAX = 10000;

interface FormValues {
  cycleId: string;
  intakeName: string;
  startDate: string;
  endDate: string;
}

function toFormValues(intake?: Intake, defaultCycleId?: number): FormValues {
  return {
    cycleId: intake ? String(intake.cycleId) : defaultCycleId ? String(defaultCycleId) : '',
    intakeName: intake?.intakeName ?? '',
    startDate: intake?.startDate ?? '',
    endDate: intake?.endDate ?? '',
  };
}

/**
 * The `path` values a 422 may name that this form can actually MARK.
 *
 * ⚠️ **These are the keys the form SENDS**, which is what a 422's `path` names;
 * `toFormValues()` is keyed on exactly those, so deriving the list from it
 * keeps it from drifting away from the controls. The catalog write schemas are
 * `.strict()`, so anything outside it — `createdBy`, `modifiedBy`, a typo —
 * belongs to no control here.
 *
 * A path can be non-empty and still bind to nothing, and that case is the worse
 * one because it looks bindable: routed by path, the message is attached to an
 * input that does not exist, disappears from the banner, and still inflates the
 * "fields need attention" count.
 */
const KNOWN_WRITE_KEYS: readonly string[] = Object.keys(toFormValues());

export function IntakeFormModal({ open, onClose, intake, onSaved, ...rest }: IntakeFormModalProps) {
  if (!open) return null;
  return (
    <IntakeForm
      key={intake?.id ?? 'new'}
      onClose={onClose}
      intake={intake}
      onSaved={onSaved}
      {...rest}
    />
  );
}

function IntakeForm({
  onClose,
  intake,
  cycles,
  cyclesLoading,
  cyclesFailed,
  defaultCycleId,
  onSaved,
}: Omit<IntakeFormModalProps, 'open'>) {
  const formId = useId();
  const nameRef = useRef<HTMLElement | null>(null);
  const isEdit = intake !== undefined;

  const baseline = useMemo(
    () => toFormValues(intake, defaultCycleId),
    [intake, defaultCycleId],
  );
  const [values, setValues] = useState<FormValues>(baseline);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const errors = useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);

  const changedKeys = useMemo(() => {
    const keys = Object.keys(baseline) as (keyof FormValues)[];
    return keys.filter((key) => values[key].trim() !== baseline[key].trim());
  }, [values, baseline]);
  const isDirty = changedKeys.length > 0;

  function setField(key: keyof FormValues, next: string) {
    setValues((prev) => ({ ...prev, [key]: next }));
    setClientErrors((prev) => (key in prev ? without(prev, key) : prev));
    setServerErrors((prev) => (key in prev ? without(prev, key) : prev));
    if (key === 'startDate') {
      setClientErrors((prev) => ('endDate' in prev ? without(prev, 'endDate') : prev));
      setServerErrors((prev) => ('endDate' in prev ? without(prev, 'endDate') : prev));
    }
  }

  /*
   * As on cycles, the server only checks `startDate <= endDate` when a PATCH
   * carries both, so both go whenever either changed. `cycleId` is re-checked
   * against d_cycles only when the patch includes it.
   */
  function buildPayload(): IntakeCreateInput | IntakeUpdateInput {
    if (intake) {
      const payload: IntakeUpdateInput = {};
      if (changedKeys.includes('cycleId')) payload.cycleId = Number(values.cycleId);
      if (changedKeys.includes('intakeName')) payload.intakeName = values.intakeName.trim();
      if (changedKeys.includes('startDate') || changedKeys.includes('endDate')) {
        payload.startDate = values.startDate.trim();
        payload.endDate = values.endDate.trim();
      }
      return payload;
    }

    return {
      cycleId: Number(values.cycleId),
      intakeName: values.intakeName.trim(),
      startDate: values.startDate.trim(),
      endDate: values.endDate.trim(),
    };
  }

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};
    const name = values.intakeName.trim();
    const start = values.startDate.trim();
    const end = values.endDate.trim();

    if (values.cycleId === '') found.cycleId = 'Choose a cycle.';
    if (name === '') found.intakeName = 'Intake name is required.';
    else if (name.length > NAME_MAX) found.intakeName = `Must be ${NAME_MAX} characters or fewer.`;

    for (const [key, value, label] of [
      ['startDate', start, 'Start date'],
      ['endDate', end, 'End date'],
    ] as const) {
      if (value === '') found[key] = `${label} is required.`;
      else if (!isRealDate(value)) found[key] = `${label} must be a real date.`;
    }

    if (!found.startDate && !found.endDate && start > end) {
      found.endDate = 'End date must be on or after the start date.';
    }

    return found;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const found = validate();
    setClientErrors(found);
    setServerErrors({});
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    const payload = buildPayload();
    if (Object.keys(payload).length === 0) {
      setFormError('Nothing has changed yet.');
      return;
    }

    setSubmitting(true);
    try {
      const result = intake
        ? await api.updateIntake(intake.id, payload)
        : await api.createIntake(payload as IntakeCreateInput);
      onSaved(result.data);
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * Split against this form's OWN field list, never against the shape of
         * the path. `unroutable` is everything no input here can carry — the
         * EMPTY-path `.strict()` and "at least one field" refusals, plus any
         * path naming a key this form does not render. Rendering it at form
         * level is the only way it is seen at all, and it is never counted as
         * highlighted.
         */
        const { fields, unroutable } = caught.routeFieldErrors(KNOWN_WRITE_KEYS);
        setServerErrors(fields);
        if (unroutable.length > 0) setFormError(unroutable.join(' '));
        else if (Object.keys(fields).length === 0) {
          if (caught.status === 422 && /cycle/i.test(caught.message)) {
            // "Cycle {id} does not exist" arrives as a bare 422 with no details
            // array, so it has to be routed to the picker by hand.
            setServerErrors({ cycleId: caught.message });
          } else {
            setFormError(caught.message);
          }
        }
      } else {
        setFormError('Could not reach the server. Check your connection.');
      }
      setSubmitting(false);
    }
  }

  /*
   * A picker that silently dropped the intake's current cycle would read as
   * unselected and reparent the record on the next save. If the cycle is not on
   * the loaded page it is added back by id — the name is unknown, but the link
   * is preserved, which is the part that matters.
   *
   * The CREATE default is pinned the same way, and for the same reason: it is
   * carried in from the page's cycle filter, so it is an id this form will send
   * whether or not the lookup produced a row for it. A `<select>` whose value
   * matches no option renders as unselected, so without this the picker would
   * show nothing while `cycleId` still went out in the payload — and with
   * `/cycles` down that is not an edge case, it is every option at once.
   */
  const cycleOptions = useMemo(() => {
    const list = cycles.map((cycle) => ({ id: cycle.id, label: cycle.cycleName }));
    const pinned = intake ? intake.cycleId : defaultCycleId;
    if (pinned !== undefined && !list.some((option) => option.id === pinned)) {
      list.unshift({ id: pinned, label: `Cycle #${pinned}` });
    }
    return list;
  }, [cycles, intake, defaultCycleId]);

  /*
   * A create that cannot name a cycle: the lookup died and the page carried no
   * default in, so `cycleOptions` is empty and there is nothing to pick.
   *
   * The required-field rule below already disables Save in that state, so this
   * does not block anything new — it exists so the reason can be SAID. A
   * disabled button whose cause is not on screen reads as a button that does
   * not work, and the alternative is worse: letting it through to fail on
   * "Choose the cycle this intake belongs to", which blames the person for a
   * choice the form never offered them.
   *
   * Deliberately NOT extended to edit, nor to a create that has a pinned cycle.
   * The lookup supplies LABELS; the id those saves send is real either way, and
   * refusing them would leave every intake uneditable here for as long as
   * `/cycles` is down — the same reasoning as the out-of-range warning above.
   */
  const noCycleSelectable = !isEdit && cyclesFailed && cycleOptions.length === 0;

  const fieldErrorCount = Object.keys(errors).length;
  const saveDisabled = isEdit
    ? !isDirty
    : noCycleSelectable ||
      values.cycleId === '' ||
      values.intakeName.trim() === '' ||
      values.startDate === '' ||
      values.endDate === '';

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      dismissOnBackdrop={false}
      initialFocusRef={nameRef}
      title={intake ? `Edit ${intake.intakeName}` : 'New intake'}
      footer={
        <>
          {isEdit && !isDirty && (
            <p className="self-center text-xs text-fg-muted sm:mr-auto">No changes yet.</p>
          )}
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={saveDisabled}
          >
            {isEdit ? 'Save changes' : 'Create intake'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Cycle" required error={errors.cycleId}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Select
                  id={id}
                  value={values.cycleId}
                  disabled={cyclesLoading}
                  invalid={Boolean(errors.cycleId)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('cycleId', event.target.value)}
                >
                  {/*
                   * Three states, and they are genuinely exclusive — the fetch
                   * is in flight, or it failed, or it answered. An empty picker
                   * is the failure's most convincing disguise, so the placeholder
                   * says which one this is rather than leaving "Choose a cycle"
                   * over nothing to choose.
                   */}
                  <option value="">
                    {cyclesLoading
                      ? 'Loading cycles…'
                      : cyclesFailed
                        ? 'Cycle list unavailable'
                        : 'Choose a cycle'}
                  </option>
                  {cycleOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <div
            className="sm:col-span-2"
            ref={(node) => {
              nameRef.current = node?.querySelector('input') ?? null;
            }}
          >
            <Field label="Intake name" required error={errors.intakeName}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={values.intakeName}
                  maxLength={NAME_MAX}
                  invalid={Boolean(errors.intakeName)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('intakeName', event.target.value)}
                />
              )}
            </Field>
          </div>

          <Field label="Start date" required error={errors.startDate}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                type="date"
                value={values.startDate}
                invalid={Boolean(errors.startDate)}
                aria-describedby={describedBy}
                onChange={(event) => setField('startDate', event.target.value)}
              />
            )}
          </Field>

          <Field label="End date" required error={errors.endDate}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                type="date"
                value={values.endDate}
                min={values.startDate || undefined}
                invalid={Boolean(errors.endDate)}
                aria-describedby={describedBy}
                onChange={(event) => setField('endDate', event.target.value)}
              />
            )}
          </Field>
        </div>

      </form>
    </Modal>
  );
}
